/**
 * Atmospheric financial chart-grid background for /login and /.
 *
 * A subtle ticker/chart grid: horizontal price lines at evenly spaced
 * "levels", vertical time markers, a couple of glowing axis labels
 * floating off-center. Reads as Bloomberg / TradingView, not AI demo.
 *
 * Pure canvas, deterministic (no animation, no per-frame work),
 * resizes on window resize, aria-hidden + pointer-events: none.
 */

import React, { useEffect, useRef } from 'react';

const ChartGridBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Canvas can't read CSS vars passively — snapshot the active theme's
    // tokens at draw time so the backdrop matches Cobalt / Plasma. (Login
    // is cobalt-only, but a logged-out Plasma user can land here.)
    const cs = getComputedStyle(document.documentElement);
    const rv = (name: string, fb: string) => cs.getPropertyValue(name).trim() || fb;
    const hexToRgba = (hex: string, a: number) => {
      const h = hex.replace('#', '');
      const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    };
    const cBg    = rv('--gv-bg', '#0a1220');
    const cBrand = rv('--gv-brand', '#4f8cff');
    const cPos   = rv('--gv-pos', '#facc15');
    const cNeg   = rv('--gv-neg', '#dc2626');
    const cGhost = rv('--gv-ghost', '#6f7a90');

    const draw = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(cssW * dpr));
      canvas.height = Math.max(1, Math.floor(cssH * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      // Background — same ground as the page so the canvas blends.
      ctx.fillStyle = cBg;
      ctx.fillRect(0, 0, cssW, cssH);

      // Horizontal price grid lines — denser near the middle (where price action is)
      const priceLines = 18;
      ctx.lineWidth = 1;
      for (let i = 1; i < priceLines; i++) {
        const y = (cssH * i) / priceLines;
        // Stronger near the middle, faint at the edges (vignette feel)
        const dist = Math.abs(i - priceLines / 2) / (priceLines / 2);
        const alpha = 0.04 + (1 - dist) * 0.05;
        ctx.strokeStyle = hexToRgba(cBrand, alpha);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(cssW, y);
        ctx.stroke();
      }

      // Vertical time markers — sparser
      const timeLines = 14;
      for (let i = 1; i < timeLines; i++) {
        const x = (cssW * i) / timeLines;
        ctx.strokeStyle = hexToRgba(cGhost, 0.04);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, cssH);
        ctx.stroke();
      }

      // A couple of subtle "key levels" — thicker lines simulating a King
      // strike (gold) + a flip line (red). Positioned off-center so the eye
      // can pick them up without the form competing with them.
      const kingY = cssH * 0.30;
      const flipY = cssH * 0.62;
      ctx.lineWidth = 1.5;
      ctx.strokeStyle = hexToRgba(cPos, 0.18);
      ctx.beginPath(); ctx.moveTo(0, kingY); ctx.lineTo(cssW, kingY); ctx.stroke();
      ctx.setLineDash([8, 6]);
      ctx.strokeStyle = hexToRgba(cNeg, 0.20);
      ctx.beginPath(); ctx.moveTo(0, flipY); ctx.lineTo(cssW, flipY); ctx.stroke();
      ctx.setLineDash([]);

      // Floating axis-style labels (no real numbers — purely atmospheric)
      ctx.fillStyle = hexToRgba(cPos, 0.40);
      ctx.font = '10px "SF Mono", Monaco, "Cascadia Code", monospace';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      ctx.fillText('★ KING', cssW - 110, kingY - 8);
      ctx.fillStyle = hexToRgba(cNeg, 0.55);
      ctx.fillText('FLIP', cssW - 110, flipY - 8);

      // Center radial vignette so the glass card stays readable.
      const grad = ctx.createRadialGradient(
        cssW / 2, cssH * 0.42, Math.min(cssW, cssH) * 0.15,
        cssW / 2, cssH * 0.42, Math.max(cssW, cssH) * 0.7,
      );
      grad.addColorStop(0,    hexToRgba(cBg, 0.85));
      grad.addColorStop(0.55, hexToRgba(cBg, 0.55));
      grad.addColorStop(1,    hexToRgba(cBg, 0));
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, cssW, cssH);
    };

    draw();
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(draw);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <div className="mc-bg" aria-hidden="true">
      <canvas ref={canvasRef} />
    </div>
  );
};

export default ChartGridBackground;
